import {
  Component, DestroyRef,
  EventEmitter, inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy
} from "@angular/core";
import { first, tap } from "rxjs/operators";
import { GroupItemModel } from "./group.interface";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { NestedTreeControl } from "@angular/cdk/tree";
import {
  HierarchyModificationDialogComponent,
} from "./hierarchy-modification-dialog/hierarchy-modification-dialog.component";
import { LorenHierarchyService } from "./services/loren-hierarchy.service";
import { HierarchyDeletionDialogComponent } from "./hierarchy-deletion-dialog/hierarchy-deletion-dialog.component";
import { LorenSnackBarService } from "../../services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenGroupService } from "../../../features/loren-groups/services/loren-group-service/loren-group.service";
import { GenericItems } from "../../interfaces/generic-items.interface";
import { LorenAsset } from "../../../features/loren-assets/interfaces/loren-asset.interface";
import { GROUPS_ALL } from "./constants/groups.constants";
import { LorenPrivilegeEntityType } from "../../../features/loren-roles/interfaces/loren-role.interface";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {DialogService} from "../../../common/dialog/dialog.service";

@Component({
  selector: "loren-hierarchy-selector",
  templateUrl: "./hierarchy-selector.component.html",
  styleUrls: ["./hierarchy-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HierarchySelectorComponent implements OnInit, OnChanges {
  private lorenHierarchyService = inject(LorenHierarchyService);
  private snackBarService = inject(LorenSnackBarService);
  private readonly dialogService = inject(DialogService);

  private readonly destroyRef = inject(DestroyRef);
  @Input() filter!: string;
  @Input() showCheckboxes = false;
  @Input() showAssets = true;
  @Input() editable = true;
  @Input() showAssetsWithoutGroup = false;
  @Input() selectedGroups: GroupItemModel[] = [];
  @Output() hierarchySelectionChange = new EventEmitter<GroupItemModel[]>();
  @Output() deleteElement = new EventEmitter<GroupItemModel>();
  @Output() addElement = new EventEmitter<GroupItemModel>();

  groups = signal<[GroupItemModel, GenericItems<LorenAsset>]>(null);
  loading = signal<boolean>(false);
  error = signal<boolean>(false);

  treeControl = new NestedTreeControl<GroupItemModel>(
    (node) => node.childGroups
  );
  dataSource = new MatTreeNestedDataSource<GroupItemModel>();
  assetsMap: Map<number, LorenAsset> = new Map<number, LorenAsset>();
  rootName = GROUPS_ALL;
  assetsWithoutGroup: LorenAsset[] = [];
  readonly privileges = LorenPrivilegeEntityType;

  private root: GroupItemModel;
  private flatGroupsList: GroupItemModel[] = [];

  ngOnInit(): void {
    this.loading.set(true);
    this.lorenHierarchyService
      .hierarchyData$()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((x) => {
          this.root = x[0];
          x[1].items.forEach((a) => this.assetsMap.set(a.id, a));
          this.assetsWithoutGroup = this.root.assetsInGroup
                       .map((a) =>
              a && this.assetsMap.has(a) ? this.assetsMap.get(a)! : null
            )
            .filter((a) => a !== null);

          if (this.showAssets) {
            this.populateTreeWithAssets(this.root);
          }

          this.dataSource.data = [this.root];
          this.populateFlatGroupsList(this.root);
          setTimeout(() => {
            this.treeControl.expandDescendants(this.dataSource.data[0]);
            this.handleSelectedGroupsState();
          }, 5);
        })
      )
      .subscribe({
        next: (res) => {
          this.error.set(false);
          this.loading.set(false);
          this.groups.set(res);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  ngOnChanges(): void {
    if (this.filter) {
      this.dataSource.data = this.searchInTree(this.root, this.filter);
    } else {
      this.dataSource.data = [this.root];
    }

    this.handleSelectedGroupsState();
  }

  openModificationDialog(node: GroupItemModel): void {
    const dialogRef = this.dialogService.open(HierarchyModificationDialogComponent, {
      width: "400px",
      data: { node },
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        first(),
      )
      .subscribe((dialogData) => {
        if (!dialogData) {
          // cancel clicked;
          return;
        }

        this.lorenHierarchyService
          .saveHierarchyData$(node, dialogData)
          .pipe(takeUntilDestroyed(this.destroyRef), first())
          .subscribe({
            next: (res) => {
              LorenGroupService.newGroupCreated$.next({
                ...(res as GroupItemModel),
                isAsset: false,
              });
              this.snackBarService.showSuccessMessage();
            },
            error: (error) => {
              this.snackBarService.showErrorMessage(error.message);
            },
          });
      });
  }

  openDeleteDialog(node: GroupItemModel): void {
    const dialogRef = this.dialogService.open(HierarchyDeletionDialogComponent, {
      width: "450px",
      data: { node },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef), first())
      .subscribe((result) => {
        if (result) {
          this.lorenHierarchyService
            .deleteHierarchyData$(node)
            .pipe(takeUntilDestroyed(this.destroyRef), first())
            .subscribe({
              next: () => {
                LorenGroupService.newGroupCreated$.next(null);
                this.snackBarService.showSuccessMessage();
              },
              error: (error) => {
                this.snackBarService.showErrorMessage(error.message);
              },
            });
        }
      });
  }

  addHierarchyItem(node: GroupItemModel) {
    this.openModificationDialog(node);
  }

  removeHierarchyItem(node: GroupItemModel) {
    this.openDeleteDialog(node);
  }

  handleGroupSelection(node: GroupItemModel) {
    this.markRecursively(node, !node.isChecked);

    const selection = this.flatGroupsList.filter((x) => x.isChecked);
    this.hierarchySelectionChange.emit(selection);
  }

  hasChild = (_: number, node: GroupItemModel) =>
    !!node.childGroups && node.childGroups.length > 0;

  private handleSelectedGroupsState() {
    // TODO temporary solution, this component should be dummy
    this.flatGroupsList.forEach((group) => (group.isChecked = false));
    this.selectedGroups.forEach((group) => {
      const foundGroup = this.flatGroupsList.find((x) => x.id === group.id);
      if (foundGroup) {
        foundGroup.isChecked = true;
      }
    });
  }

  private markRecursively(root: GroupItemModel, state: boolean) {
    root.isChecked = state;

    if (root.childGroups && root.childGroups.length > 0) {
      root.childGroups.forEach((x) => this.markRecursively(x, state));
    }
  }

  private searchInTree(
    tree: GroupItemModel,
    searchString: string
  ): GroupItemModel[] {
    const results: GroupItemModel[] = [];

    // Check if the current node's name includes the searchString
    if (tree.name.toLowerCase().includes(searchString.toLowerCase())) {
      results.push(tree);
    }

    // Recursively search in child groups
    if (tree.childGroups) {
      for (const child of tree.childGroups) {
        results.push(...this.searchInTree(child, searchString));
      }
    }

    return results;
  }

  private populateTreeWithAssets(root: GroupItemModel) {
    // don't add assets in root node of the tree
    if (root.id !== this.root.id) {
      root.assetsInGroup.forEach((x) => {
        const asset: LorenAsset = this.assetsMap.get(x);

        if (!asset) {
          // do not add assets that don't belong to you but are in your group
          return;
        }

        root.childGroups.push({
          id: asset.id,
          isAsset: true,
          assetsInGroup: [],
          parentGroupId: root.id,
          name: asset.name,
          description: asset.description,
        });
      });
    }

    const groupsOnly = root.childGroups.filter((x) => !x.isAsset);
    if (groupsOnly && groupsOnly.length > 0) {
      groupsOnly.forEach((x) => {
        this.populateTreeWithAssets(x);
      });
    }
  }

  private populateFlatGroupsList(root: GroupItemModel) {
    this.flatGroupsList.push(root);
    if (root.childGroups && root.childGroups.length > 0) {
      root.childGroups.forEach((x) => {
        this.populateFlatGroupsList(x);
      });
    }
  }
}
